import { MdCheck } from 'react-icons/md';
import { useEffect, useMemo } from 'react';
import { ReportingControllerBaseConfig } from '../../../../../base';
import { EnumElementController } from './enumElementInterface';
import { EnumElementViewProps } from './enumElementProps';
import { OptionItemProps } from './option/optionItemProps';
import { buildOptionItemProps } from './option/optionItemFactory';
import { assert, keyBy } from '@varos/util-typescript';
import { generated } from '@varos/research-sdk-api';

export function createEnumElementController(
    config: ReportingControllerBaseConfig
): EnumElementController {
    return {
        useProps(item, props): EnumElementViewProps {
            const optionByValue = useMemo(
                () => keyBy(item.property.config.options, 'key'),
                [item.property.config.options]
            );

            const mappedOptions = useMemo(
                () => item.property.config.options.map(buildOptionItemProps),
                [item.property.config.options]
            );

            const selectedOption = props.value
                ? (optionByValue[props.value] ?? null)
                : null;
            const mappedValue = selectedOption
                ? buildOptionItemProps(selectedOption)
                : null;

            return {
                containerRef: props.containerRef,
                isEmpty: props.value === null,
                edit: {
                    value: props.isEditing,
                    button: {
                        onClick() {
                            props.onEdit();
                        },
                    },
                },
                label: props.value
                    ? (optionByValue[props.value]?.display_name ?? 'Unknown')
                    : 'Empty',
                value: mappedValue,
                input: {
                    isMulti: false,
                    options: mappedOptions,
                    value: mappedValue,
                    onMenuClose() {
                        props.onBlur();
                    },
                    onChange(option, metadata) {
                        if (metadata.action === 'select-option') {
                            assert(option?.id, 'Option must have an id');
                            // Find the original option by id to get the key
                            const originalOption = item.property.config.options.find(
                                (o) => o.id === option.id
                            );
                            assert(
                                originalOption?.key,
                                'Original option must have a key'
                            );
                            props.onChange(originalOption.key);
                            return;
                        }
                        if (metadata.action === 'create-option') {
                            assert(option?.label, 'Option must have a label');
                            props.onOptionCreate({
                                label: option.label,
                            });
                            return;
                        }
                        if (metadata.action === 'clear') {
                            props.onChange(null);
                            return;
                        }
                        if (metadata.action === 'remove-value') {
                            props.onChange(null);
                            return;
                        }
                        if (metadata.action === 'pop-value') {
                            props.onChange(null);
                            return;
                        }
                        if (metadata.action === 'deselect-option') {
                            props.onChange(null);
                            return;
                        }
                        throw new Error('Unsupported enum action');
                    },
                    closeMenuOnSelect: true,
                    placeholder: 'Search for an option',
                },
            };
        },
    };
}
